// ============================================
// Progress Card — shared renderer
// ============================================
//
// Single component that renders one storyline card. Consumed by the Phone
// Progress App's Storylines tab AND the Journal's Progress tab. Layout +
// classes use the phone's existing namespace (`.completion-card-*`) so the
// CSS lives in `_09-phone-overlay.css`.
//
// Five visual states by `sl.status`:
//   hidden    — '???' label, no progress, dimmed
//   active    — normal pink fill + val/max badge
//   complete  — green border + ✓ Done badge
//   pending   — yellow tint, OPTION badge, "Decision upcoming" implied via 0%
//   locked    — 45% opacity, strike-through, ✕ LOCKED badge
//
// Set `sl.isPathCard = true` for indented multi-path sub-cards.
// Provide `opts.onClick` to make the card click-through to a detail view.

export function renderProgressCard(container, sl, opts = {}) {
    const showSpoilers = !!opts.showSpoilers;
    const onClick = opts.onClick || null;

    const card = document.createElement('div');
    card.className = 'completion-card';
    if (sl.status === 'hidden') card.classList.add('completion-card-hidden');
    else if (sl.status === 'complete') card.classList.add('completion-card-complete');
    else if (sl.status === 'pending') card.classList.add('completion-card-pending');
    else if (sl.status === 'locked') card.classList.add('completion-card-locked');
    if (sl.isPathCard) card.classList.add('completion-card-path');
    if (onClick) card.classList.add('completion-card-clickable');

    const pct = (sl.status === 'pending' || sl.status === 'locked')
        ? 0
        : (sl.max > 0 ? Math.min(100, Math.max(0, (sl.val / sl.max) * 100)) : 0);
    const done = sl.status === 'complete';

    // Header: icon + name + warning badge left, status badge right
    const iconHtml = sl.icon ? `${sl.icon} ` : '';
    let headerHTML = `<div class="completion-storyline-header">
        <div class="completion-storyline-header-left">
            <span class="completion-storyline-name">${iconHtml}${sl.name}</span>
            ${sl.warning ? '<span class="completion-badge-warning">⚠</span>' : ''}
        </div>`;
    if (sl.status === 'hidden') {
        headerHTML += `<span class="completion-storyline-badge completion-badge-hidden">???</span>`;
    } else if (done) {
        headerHTML += `<span class="completion-storyline-badge completion-badge-complete">✓ Done</span>`;
    } else if (sl.status === 'pending') {
        headerHTML += `<span class="completion-storyline-badge completion-badge-pending">option</span>`;
    } else if (sl.status === 'locked') {
        headerHTML += `<span class="completion-storyline-badge completion-badge-locked">✕ locked</span>`;
    } else {
        headerHTML += `<span class="completion-storyline-badge">${sl.val}/${sl.max}</span>`;
    }
    headerHTML += `</div>`;

    let html = headerHTML;

    // Progress bar
    if (sl.status !== 'hidden') {
        const barColor = done ? '#5cd89c' : 'var(--orchid)';
        html += `<div class="completion-wide-track" style="margin-top:4px;margin-bottom:4px;">
            <div class="completion-wide-fill" style="width:${pct}%;background:${barColor};"></div>
        </div>`;
    }

    // Description
    if (sl.desc) {
        html += `<div class="completion-storyline-desc">${sl.desc}</div>`;
    }

    // Milestones as compact chips
    if (sl.milestones && sl.milestones.length > 0 && sl.status !== 'hidden') {
        html += '<div class="completion-milestone-row">';
        for (let i = 0; i < sl.milestones.length; i++) {
            const mDone = i < sl.milestoneCurrent;
            const mNext = i === sl.milestoneCurrent && sl.status === 'active';
            let chipClass = 'completion-milestone-chip';
            if (mDone) chipClass += ' completion-milestone-chip-done';
            else if (mNext) chipClass += ' completion-milestone-chip-next';
            html += `<span class="${chipClass}">${mDone ? '✓ ' : ''}${sl.milestones[i]}</span>`;
        }
        html += '</div>';
    }

    // Warning
    if (sl.warning) {
        html += `<div class="completion-warning-text">⚠ ${sl.warning}</div>`;
    }

    // Hint — supports raw text OR pre-formatted HTML (journal passes pre-wrapped <div>).
    if (sl.hint && !done) {
        const isHtml = typeof sl.hint === 'string' && sl.hint.trim().startsWith('<');
        if (isHtml) {
            html += sl.hint;
        } else {
            html += `<div class="completion-storyline-hint">→ ${sl.hint}</div>`;
        }
    }

    // Spoiler (gated by caller — phone has a toggle, journal currently always shows)
    if (showSpoilers && sl.spoiler) {
        html += `<div class="completion-spoiler-text">${sl.spoiler}</div>`;
    }

    card.innerHTML = html;
    if (onClick) {
        card.addEventListener('click', () => onClick(sl));
    }
    container.appendChild(card);
    return card;
}

/** Render a storyline with multi-path support. Pre-decision: base card alone.
 *  Decision live but no arc: base + N pending previews. Arc chosen: 1 active
 *  + N-1 locked siblings (no base).
 *
 *  sl must include: name, desc, val, max, status, optional warning/milestones/spoiler.
 *  Multi-path requires: paths[] (with key/name/desc/activeWhen/val/max/hint),
 *  decisionUnlocked() function.
 */
export function renderStorylineGroup(container, sl, opts = {}) {
    if (!sl.paths) {
        renderProgressCard(container, sl, opts);
        return;
    }
    const activePath = sl.paths.find(p => p.activeWhen());
    if (!activePath) {
        // Base card pre-decision
        renderProgressCard(container, sl, opts);
        if (sl.decisionUnlocked && sl.decisionUnlocked()) {
            for (const p of sl.paths) {
                renderProgressCard(container, {
                    name: p.name, icon: p.icon || sl.icon,
                    val: 0, max: p.max,
                    desc: p.desc, hint: '',
                    status: 'pending', isPathCard: true,
                }, opts);
            }
        }
    } else {
        // Active path + locked siblings
        for (const p of sl.paths) {
            const isActive = p === activePath;
            const v = isActive ? p.val() : 0;
            renderProgressCard(container, {
                name: p.name, icon: p.icon || sl.icon,
                val: v, max: p.max,
                desc: p.desc, hint: isActive ? p.hint : '',
                status: isActive ? (v >= p.max ? 'complete' : 'active') : 'locked',
                isPathCard: true,
            }, opts);
        }
    }
}
